import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { AddressDetailDTO, AddressDetailUpdateRequestDTO, AddressDetailCreateRequestDTO } from '../address-detail/address-detail.interface';
import { AddressDetailService } from '../address-detail/address-detail.service';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';

@Component({
  selector: 'app-uic-address-detail-view',
  templateUrl: './uic-address-detail-view.component.html',
  styleUrls: ['./uic-address-detail-view.component.css']
})
export class UicAddressDetailViewComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() set addressId(value: number) {
    this._addressId = value;
    if (value > 0) {
      this.loadAddressDetails();
    } else {
      this.resetComponent();
    }
  }
  get addressId(): number {
    return this._addressId;
  }

  private _addressId: number = 0;
  @Input() masterType: string = '';
  @Input() masterId: number = 0;
  @Input() isNewAddress: boolean = false;
  @Output() onAddressUpdate = new EventEmitter<AddressDetailDTO>();
  @Output() onAddressCreated = new EventEmitter<AddressDetailDTO>();
  @Output() onEditStateChange = new EventEmitter<boolean>();
  @Output() onCancelEdit = new EventEmitter<void>();

  addressDetailService = inject(AddressDetailService);
  userService = inject(UserLoginService);
  User = this.userService.User();

  addressForm: FormGroup = new FormGroup({});
  addressData: AddressDetailDTO | null = null;
  isLoading: boolean = false;
  isEditing: boolean = false;
  isSaving: boolean = false;

  ngOnInit(): void {
    this.initializeForm();

    // If it's a new address, enable editing mode immediately
    if (this.isNewAddress) {
      this.isEditing = true;
      this.addressForm.enable();
      this.onEditStateChange.emit(true);
    }
  }

  resetComponent(): void {
    this.addressData = null;
    this.isEditing = false;
    this.isSaving = false;
    this.addressForm.reset();
    this.addressForm.disable();
  }

  initializeForm(): void {
    this.addressForm = new FormGroup({
      addressType: new FormControl('', [Validators.required]),
      address: new FormControl('', [Validators.required]),
      city: new FormControl('', [Validators.required]),
      state: new FormControl('', [Validators.required]),
      country: new FormControl(''),
      pincode: new FormControl('', [Validators.required]),
      gstNo: new FormControl(''),
      isDefault: new FormControl(0),
      // Not shown in this view's UI, but must round-trip: createAddress()/updateAddress()
      // below read formValue.otherDetail1-6 and send them on every save. Without these
      // controls the form value never had the property, so every update from this
      // component (the Sales Order/Challan/Invoice Billing-Delivery address picker)
      // silently sent '' and wiped out whatever was stored there.
      otherDetail1: new FormControl(''),
      otherDetail2: new FormControl(''),
      otherDetail3: new FormControl(''),
      otherDetail4: new FormControl(''),
      otherDetail5: new FormControl(''),
      otherDetail6: new FormControl('')
    });

    // Disable form by default until user clicks edit
    this.addressForm.disable();
  }

  loadAddressDetails(): void {
    this.isLoading = true;
    const requestData = {
      addressId: this.addressId,
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId
    };
    this.addressDetailService.AddressDetail_readById(requestData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.addressData = res;
          this.patchFormValues(res);
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading address details:', err);
          this.isLoading = false;
        }
      });
  }

  patchFormValues(data: AddressDetailDTO): void {
    this.addressForm.patchValue({
      addressType: data.addressType || '',
      address: data.address || '',
      city: data.city || '',
      state: data.state || '',
      country: data.country || '',
      pincode: data.pincode || '',
      gstNo: data.gstNo || '',
      isDefault: data.isDefault || 0,
      otherDetail1: data.otherDetail1 || '',
      otherDetail2: data.otherDetail2 || '',
      otherDetail3: data.otherDetail3 || '',
      otherDetail4: data.otherDetail4 || '',
      otherDetail5: data.otherDetail5 || '',
      otherDetail6: data.otherDetail6 || ''
    });
  }

  toggleEdit(): void {
    this.isEditing = !this.isEditing;
    this.onEditStateChange.emit(this.isEditing);
    if (this.isEditing) {
      this.addressForm.enable();
    } else {
      this.addressForm.disable();
      // Revert to original values if canceling
      if (this.addressData) {
        this.patchFormValues(this.addressData);
      }
    }
  }

  onSave(): void {
    if (this.addressForm.invalid) {
      Object.keys(this.addressForm.controls).forEach(key => {
        const control = this.addressForm.get(key);
        if (control && control.invalid) {
          control.markAsTouched();
        }
      });
      return;
    }

    this.isSaving = true;
    const formValue = this.addressForm.value;

    // Check if creating new or updating existing
    if (this.isNewAddress || this.addressId === 0) {
      this.createAddress(formValue);
    } else {
      this.updateAddress(formValue);
    }
  }

  createAddress(formValue: any): void {
    const createData: AddressDetailCreateRequestDTO = {
      masterId: this.masterId,
      masterType: this.masterType,
      addressType: formValue.addressType,
      address: formValue.address,
      city: formValue.city,
      state: formValue.state,
      country: formValue.country || '',
      pincode: formValue.pincode,
      status: 1,
      isDefault: formValue.isDefault,
      gstNo: formValue.gstNo || '',
      otherDetail1: formValue.otherDetail1 || '',
      otherDetail2: formValue.otherDetail2 || '',
      otherDetail3: formValue.otherDetail3 || '',
      otherDetail4: formValue.otherDetail4 || '',
      otherDetail5: formValue.otherDetail5 || '',
      otherDetail6: formValue.otherDetail6 || '',
      actionUser: this.User.userId.toString(),
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId
    };

    this.addressDetailService.AddressDetail_create(createData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.addressData = res;
          this.patchFormValues(res);
          this.onAddressCreated.emit(res);
          this.isEditing = false;
          this.onEditStateChange.emit(false);
          this.addressForm.disable();
          this.isSaving = false;
          console.log('Address created successfully');
        },
        error: (err) => {
          console.error('Error creating address:', err);
          this.isSaving = false;
        }
      });
  }

  updateAddress(formValue: any): void {

    const updateData: AddressDetailUpdateRequestDTO = {
      addressId: this.addressId,
      masterId: this.addressData?.masterId || 0,
      masterType: this.addressData?.masterType || this.masterType,
      addressType: formValue.addressType,
      address: formValue.address,
      city: formValue.city,
      state: formValue.state,
      country: formValue.country || '',
      pincode: formValue.pincode,
      status: this.addressData?.status || 1,
      isDefault: formValue.isDefault,
      gstNo: formValue.gstNo || '',
      otherDetail1: formValue.otherDetail1 || '',
      otherDetail2: formValue.otherDetail2 || '',
      otherDetail3: formValue.otherDetail3 || '',
      otherDetail4: formValue.otherDetail4 || '',
      otherDetail5: formValue.otherDetail5 || '',
      otherDetail6: formValue.otherDetail6 || '',
      isActive: 1,
      actionUser: this.User.userId.toString(),
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId
    };

    this.addressDetailService.AddressDetail_update(updateData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.addressData = res;
          this.patchFormValues(res);
          this.onAddressUpdate.emit(res);
          this.isEditing = false;
          this.onEditStateChange.emit(false);
          this.addressForm.disable();
          this.isSaving = false;
          console.log('Address updated successfully');
        },
        error: (err) => {
          console.error('Error updating address:', err);
          this.isSaving = false;
        }
      });
  }

  onCancel(): void {
    if (this.isNewAddress) {
      // Emit cancel event for new address creation
      this.onCancelEdit.emit();
    } else {
      // For existing address, just revert changes
      this.isEditing = false;
      this.onEditStateChange.emit(false);
      this.addressForm.disable();
      if (this.addressData) {
        this.patchFormValues(this.addressData);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
